import React from 'react';
import { createPortal } from 'react-dom';
import { getEstadoChipClass, getMotivoChipClass } from '../../utils/styles'; 

/**
 * ModalDetalleContrato - Visualización detallada de un contrato ART.
 * Sincronizado con los campos de FormularioArt (Snapshots de nombres).
 */
const ModalDetalleContrato = ({ show, onClose, contrato }) => {
  if (!show || !contrato) return null;

  // Helper para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return null;
    if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
    if (typeof dateString === 'string' && dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
            const [y, m, d] = parts;
            return `${d}/${m}/${y}`;
        }
    }
    return dateString;
  };

  // Helper para formatear moneda
  const formatMoney = (value) => {
      if (!value && value !== 0) return null;
      const numberVal = typeof value === 'string' ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : value;
      return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numberVal);
  };

  // Helper para mostrar porcentajes con coma
  const formatPercent = (value) => {
    if (value === undefined || value === null || value === '') return '';
    // Asegura que 3.5 se vea como "3,5"
    return String(value).replace('.', ',');
  };

  // Helper visual para mostrar un dato estándar
  const DetailItem = ({ label, value }) => (
    <div>
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">{label}</p>
      <div className="text-base font-medium text-gray-800 break-words min-h-[1.5rem] text-left">
        {value || <span className="text-gray-300 italic text-sm">Sin dato</span>}
      </div>
    </div>
  );

  // Helper específico para importes
  const CurrencyItem = ({ label, currency, amount, isNegative }) => (
    <div>
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">{label}</p>
      <div className="text-base font-medium text-gray-800 break-words min-h-[1.5rem] text-left">
        {(amount || amount === 0) ? (
            <span>
                <span className={`font-bold mr-1.5 ${isNegative ? 'text-red-600' : 'text-blue-600'}`}>
                    {currency || '$'} {isNegative ? '-' : ''}
                </span>
                {formatMoney(amount)}
            </span>
        ) : (
            <span className="text-gray-300 italic text-sm">Sin dato</span>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[90vh] flex flex-col overflow-hidden animate-fade-in border border-gray-100">
        
        {/* Header */}
        <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 shrink-0 bg-white text-left">
            <div className="text-left">
                <h3 className="text-2xl font-bold text-gray-800">Detalle de Contrato ART</h3>
                <div className="mt-2 flex items-center gap-3">
                    <span className="bg-gray-100 px-3 py-1 rounded-md border border-gray-200 font-mono font-bold text-gray-800 text-lg">
                        #{contrato.nContrato || 'Pendiente'}
                    </span>
                    <span className={`font-bold px-3 py-1 rounded-md text-sm uppercase ${getEstadoChipClass(contrato.estadoPoliza)}`}>
                        {contrato.estadoPoliza || 'S/D'}
                    </span>
                </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-50">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
        </div>

        {/* Content Scrollable */}
        <div className="overflow-y-auto p-8 flex-grow bg-white space-y-8 custom-scrollbar text-left">

            {/* --- SECCIÓN OCULTA: ANULACIÓN --- */}
            {(contrato.estadoPoliza === 'Anulada' || contrato.estadoPoliza === 'ANULADA') && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 relative">
                     <legend className="px-3 py-1 text-sm font-bold text-red-700 bg-red-100 rounded-full border border-red-200 absolute -top-3 left-4 flex items-center shadow-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        DATOS DE ANULACIÓN
                     </legend>
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                        <DetailItem label="Motivo Anulación" value={contrato.motivoAnulacion} />
                        <DetailItem label="Fecha Anulación" value={formatDate(contrato.fechaAnulacion)} />
                        <DetailItem label="¿Quién Gestionó?" value={contrato.quienGestionoAnulacion} />
                        <div className="md:col-span-3">
                             <DetailItem label="Observaciones" value={contrato.observacionesAnulacion} />
                        </div>
                     </div>
                </div>
            )}

            {/* 1. DETALLES DE EMISIÓN */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-2">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Detalles de la Emisión</legend>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 pt-2">
                    <DetailItem label="Fecha de Carga" value={formatDate(contrato.fechaCarga)} />
                    <DetailItem label="N° Contrato" value={contrato.nContrato} />
                    
                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Tipo de Operación</p>
                        <div className="mt-1">
                        {contrato.tipoOperacion ? (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium uppercase ${getMotivoChipClass(contrato.tipoOperacion)}`}>
                                {contrato.tipoOperacion}
                            </span>
                        ) : <span className="text-gray-300 italic text-sm">Sin dato</span>}
                        </div>
                    </div>

                    <DetailItem label="Inicio Vigencia" value={formatDate(contrato.inicioVigencia)} />
                    <DetailItem label="Fin Vigencia" value={formatDate(contrato.finVigencia)} />
                </div>
            </fieldset>

            {/* 2. DATOS DEL CONTRATO */}
            <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/20">
                <legend className="px-2 text-lg font-bold text-orange-600 bg-white border border-orange-100 rounded-lg absolute -top-3 left-4 shadow-sm">
                    Datos del Contrato
                </legend>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2">
                    <DetailItem label="CIIU" value={contrato.ciiu} />
                    <DetailItem label="Cápitas (Empleados)" value={contrato.capitas} />
                    <CurrencyItem label="Masa Salarial" amount={contrato.masaSalarial} />
                    <DetailItem label="Alícuota" value={contrato.alicuota ? `${formatPercent(contrato.alicuota)}%` : null} />
                </div>
            </fieldset>

            {/* 3. COMPAÑÍA & EQUIPO */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Compañía & Equipo Comercial</legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                    <DetailItem label="Compañía" value={contrato.compania} />
                    <DetailItem label="Organizador" value={contrato.organizador} />
                    <DetailItem label="PAS" value={contrato.pas} />
                    
                    <div className="col-span-3 border-t border-gray-100 my-2"></div>

                    <DetailItem label="Canal" value={contrato.canal} />
                    <DetailItem label="Nombre Canal" value={contrato.canalNombre} />
                    <DetailItem label="Subcanal Asoc." value={contrato.subcanalNombre} />

                    <DetailItem label="Vendedor" value={contrato.vendedor} />
                    <DetailItem label="Suscriptor" value={contrato.suscriptor} />
                    <DetailItem label="Contador Asociado" value={contrato.contadorNombre} />
                </div>
            </fieldset>

            {/* 4. VALORES & COMISIONES */}
            <fieldset className="border border-indigo-200 rounded-xl p-6 relative mt-4 bg-indigo-50/20">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">
                    Valores y Comisiones
                </legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                    <DetailItem label="Tipo Conversión" value={contrato.tipoConversion} />
                    <CurrencyItem label="Prima Emitida" amount={contrato.primaEmitida} />
                    <DetailItem label="% Comisión Total" value={contrato.porcentajeComision ? `${formatPercent(contrato.porcentajeComision)}%` : null} />
                </div>
            </fieldset>

            {/* 5. DESPACHO */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Despacho</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    <DetailItem label="Fecha Despacho" value={formatDate(contrato.fechaDespacho)} />
                    <DetailItem label="Forma Despacho" value={contrato.formaDespacho} />
                </div>
            </fieldset>

        </div>
        
        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 flex justify-end bg-gray-50 shrink-0">
            <button onClick={onClose} className="px-8 py-2.5 rounded-full bg-white border border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition shadow-sm uppercase text-xs tracking-widest active:scale-95">
                Cerrar
            </button>
        </div>

      </div>
      <style>{`
        .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
      `}</style>
    </div>,
    document.body
  );
};

export default ModalDetalleContrato;